import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Play, Pause } from "lucide-react";
import { Button } from "../ui/button";
import { RecordingData } from "../../hooks/useMediaRecorder";
import { cn } from "../../lib/utils";

interface AutoPlayMediaProps {
  audio?: RecordingData;
  video?: RecordingData;
  text?: string;
  shouldAutoPlay: { audio: boolean; video: boolean };
  isPlaying: { audio: boolean; video: boolean };
  onPlayingChange: (type: "audio" | "video", playing: boolean) => void;
  onEnded: (type: "audio" | "video") => void;
  onClose: () => void;
}

export function AutoPlayMedia({
  audio,
  video,
  text,
  shouldAutoPlay,
  isPlaying,
  onPlayingChange,
  onEnded,
  onClose,
}: AutoPlayMediaProps) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [hasStartedAudio, setHasStartedAudio] = useState(false);
  const [hasStartedVideo, setHasStartedVideo] = useState(false);
  const [audioFinished, setAudioFinished] = useState(false);
  const [autoplayFailed, setAutoplayFailed] = useState({ audio: false, video: false });
  const [isPanelVisible, setIsPanelVisible] = useState(false);

  useEffect(() => {
    if (shouldAutoPlay.audio || shouldAutoPlay.video) {
      setIsPanelVisible(true);
    }
  }, [shouldAutoPlay.audio, shouldAutoPlay.video]);

  useEffect(() => {
    if (shouldAutoPlay.audio && audio?.url && !hasStartedAudio && !isPlaying.audio) {
      setHasStartedAudio(true);
      const audioEl = audioRef.current;
      if (audioEl) {
        audioEl.play()
          .then(() => {
            onPlayingChange("audio", true);
            setAutoplayFailed((prev) => ({ ...prev, audio: false }));
          })
          .catch((error) => {
            console.warn("Audio autoplay failed:", error);
            setAutoplayFailed((prev) => ({ ...prev, audio: true }));
            setAudioFinished(true);
          });
      }
    }
  }, [shouldAutoPlay.audio, audio?.url, hasStartedAudio, isPlaying.audio, onPlayingChange]);

  useEffect(() => {
    const hasAudio = shouldAutoPlay.audio && audio?.url;
    const shouldStartVideo = shouldAutoPlay.video && video?.url && !hasStartedVideo && !isPlaying.video;

    if (shouldStartVideo && (!hasAudio || audioFinished)) {
      setHasStartedVideo(true);
      const videoEl = videoRef.current;
      if (videoEl) {
        videoEl.play()
          .then(() => {
            onPlayingChange("video", true);
            setAutoplayFailed((prev) => ({ ...prev, video: false }));
          })
          .catch((error) => {
            console.warn("Video autoplay failed:", error);
            setAutoplayFailed((prev) => ({ ...prev, video: true }));
          });
      }
    }
  }, [shouldAutoPlay.video, video?.url, hasStartedVideo, isPlaying.video, onPlayingChange, shouldAutoPlay.audio, audio?.url, audioFinished]);

  useEffect(() => {
    if (!shouldAutoPlay.audio && !shouldAutoPlay.video) {
      setHasStartedAudio(false);
      setHasStartedVideo(false);
      setAudioFinished(false);
      setIsPanelVisible(false);
      setAutoplayFailed({ audio: false, video: false });
    }
  }, [shouldAutoPlay.audio, shouldAutoPlay.video]);

  const handleAudioEnded = () => {
    onPlayingChange("audio", false);
    setAudioFinished(true);
    onEnded("audio");
  };

  const handleVideoEnded = () => {
    onPlayingChange("video", false);
    onEnded("video");
  };

  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    onPlayingChange("audio", false);
    onPlayingChange("video", false);
    setIsPanelVisible(false);
    onClose();
  };

  const toggleAudio = () => {
    const audioEl = audioRef.current;
    if (!audioEl) return;

    if (isPlaying.audio) {
      audioEl.pause();
      onPlayingChange("audio", false);
    } else {
      audioEl.play()
        .then(() => {
          onPlayingChange("audio", true);
          setAutoplayFailed((prev) => ({ ...prev, audio: false }));
        })
        .catch(console.error);
    }
  };

  const hasAudio = audio?.url;
  const hasVideo = video?.url;
  const hasText = text && text.trim().length > 0;
  const hasContent = hasAudio || hasVideo || hasText;

  if (!isPanelVisible || !hasContent) {
    return null;
  }

  return (
    <>
      {hasAudio && (
        <audio
          ref={audioRef}
          src={audio.url}
          onEnded={handleAudioEnded}
          onError={() => console.error("Audio playback error")}
          preload="auto"
        />
      )}

      <div className="fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] max-h-[60vh] rounded-lg overflow-hidden shadow-2xl border bg-background flex flex-col animate-in slide-in-from-bottom-4 duration-300">
        {/* Close button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClose}
          className="absolute top-1 right-1 z-10 w-6 h-6 p-0 bg-background/80 hover:bg-background"
          title={t("close", "Close")}
        >
          <X className="w-3.5 h-3.5" />
        </Button>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Text */}
          {hasText && (
            <div className={cn("p-2 pr-8 text-sm", (hasAudio || hasVideo) && "border-b")}>
              <div
                className="prose prose-sm max-w-none [&>p]:m-0 [&>p:not(:last-child)]:mb-1"
                dangerouslySetInnerHTML={{ __html: text }}
              />
            </div>
          )}

          {/* Audio */}
          {hasAudio && (
            <div className={cn("flex items-center gap-2 p-2", hasVideo && "border-b")}>
              <Button
                variant="outline"
                size="sm"
                onClick={toggleAudio}
                className={cn(
                  "w-8 h-8 p-0 rounded-full shrink-0",
                  isPlaying.audio && "bg-primary/5 border-primary/30"
                )}
              >
                {isPlaying.audio ? (
                  <Pause className="w-3.5 h-3.5 text-primary" />
                ) : (
                  <Play className="w-3.5 h-3.5 text-primary ml-0.5" />
                )}
              </Button>
              <span className="text-xs text-muted-foreground">
                {isPlaying.audio ? t("playing", "Playing...") : t("click_to_play", "Click to play")}
              </span>
              {isPlaying.audio && (
                <span className="flex w-2 h-2 ml-auto">
                  <span className="absolute inline-flex w-2 h-2 bg-primary/60 rounded-full opacity-75 animate-ping" />
                  <span className="relative inline-flex w-2 h-2 bg-primary rounded-full" />
                </span>
              )}
            </div>
          )}

          {/* Video */}
          {hasVideo && (
            <div className="relative">
              {isPlaying.video && (
                <span className="absolute top-2 left-2 z-10 flex w-2 h-2">
                  <span className="absolute inline-flex w-2 h-2 bg-purple-400 rounded-full opacity-75 animate-ping" />
                  <span className="relative inline-flex w-2 h-2 bg-purple-500 rounded-full" />
                </span>
              )}
              <video
                ref={videoRef}
                src={video.url}
                onEnded={handleVideoEnded}
                onError={() => console.error("Video playback error")}
                controls
                controlsList="nodownload"
                className="w-full aspect-video"
                preload="auto"
              />
            </div>
          )}
        </div>

        {/* Autoplay failed */}
        {(autoplayFailed.audio || autoplayFailed.video) && !isPlaying.audio && !isPlaying.video && (
          <div className="px-2 py-1.5 border-t bg-amber-50 dark:bg-amber-950">
            <p className="text-xs text-amber-700 dark:text-amber-300">
              {t("autoplay_blocked", "Autoplay blocked. Click play.")}
            </p>
          </div>
        )}
      </div>
    </>
  );
}
